import { GeoSearchSelect } from "@/components/geo/GeoSearchSelect";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PartyFieldLabel } from "@/components/parties/PartyFormSection";
import { toast } from "@/components/ui/sonner";
import {
  createState,
  fetchCountries,
  fetchStates,
  type CountryOption,
  type StateOption,
} from "@/lib/geo/geoStore";
import { isAxiosError } from "axios";
import { MapPin, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Props = {
  country: number | null;
  state: number | null;
  onChange: (next: { country: number | null; state: number | null }) => void;
  disabled?: boolean;
};

function stateErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Could not save the state. Please try again.";
}

export function AddressGeoFields({ country, state, onChange, disabled }: Props) {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedCountry = useMemo(
    () => countries.find((row) => row.id === country) ?? null,
    [countries, country],
  );

  useEffect(() => {
    let mounted = true;
    fetchCountries()
      .then((rows) => {
        if (mounted) setCountries(rows);
      })
      .catch(() => {
        if (mounted) setCountries([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (country == null && countries.length > 0) {
      onChange({ country: countries[0].id, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, country]);

  useEffect(() => {
    let mounted = true;
    if (!country) {
      setStates([]);
      return;
    }
    setStatesLoading(true);
    fetchStates(country)
      .then((rows) => {
        if (mounted) setStates(rows);
      })
      .catch(() => {
        if (mounted) setStates([]);
      })
      .finally(() => {
        if (mounted) setStatesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [country]);

  const handleSaveState = async () => {
    if (!country) {
      toast.error("Select a country first.");
      return;
    }
    const name = newName.trim();
    if (!name) {
      toast.error("State name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await createState({
        countryId: country,
        name,
        code: newCode.trim() || null,
      });
      setStates((prev) => {
        if (prev.some((row) => row.id === created.id)) {
          return prev.map((row) => (row.id === created.id ? created : row));
        }
        return [...prev, created].sort((a, b) => a.name.localeCompare(b.name));
      });
      onChange({ country, state: created.id });
      setAddOpen(false);
      setNewName("");
      setNewCode("");
      toast.success("State saved.");
    } catch (error) {
      toast.error(stateErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="space-y-2">
        <PartyFieldLabel required>Country</PartyFieldLabel>
        <GeoSearchSelect
          value={country != null ? String(country) : ""}
          options={countries}
          onChange={(id) => onChange({ country: Number(id), state: null })}
          placeholder={countries.length === 0 ? "Loading countries…" : "Select country"}
          searchPlaceholder="Search country…"
          emptyText="No country found."
          disabled={disabled || countries.length === 0}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <PartyFieldLabel required>State/Province</PartyFieldLabel>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            disabled={disabled || !country}
            onClick={() => {
              setNewName("");
              setNewCode("");
              setAddOpen(true);
            }}
            aria-label="Add state"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        <GeoSearchSelect
          value={state != null ? String(state) : ""}
          options={states}
          onChange={(id) => onChange({ country, state: Number(id) })}
          placeholder={!country ? "Select country first" : statesLoading ? "Loading states…" : "Select state"}
          searchPlaceholder="Search state…"
          emptyText="No state found."
          disabled={disabled || !country || statesLoading}
        />
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md w-[95vw] z-[240] p-6" overlayClassName="z-[239]">
          <DialogHeader className="pb-3 border-b border-border/50">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-600">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-foreground">Add State/Province</DialogTitle>
                <p className="text-xs text-muted-foreground">Create a new state for the selected country.</p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-xl border border-border/60 bg-muted/10 p-4 space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-foreground">Country:</span>
                  <span className="font-extrabold text-primary text-sm">
                    {selectedCountry?.name ?? "Select a country first"}
                  </span>
                </div>
                {selectedCountry?.iso2 ? (
                  <span className="text-[11px] text-muted-foreground">{selectedCountry.iso2}</span>
                ) : null}
              </div>
              <p className="text-[11px] text-muted-foreground">
                This state will be saved under {selectedCountry?.name ?? "the selected country"}.
              </p>
            </div>

            <div className="space-y-2">
              <PartyFieldLabel required>State/Province Name</PartyFieldLabel>
              <Input
                className="glass-input h-10"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={saving}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <PartyFieldLabel>Code</PartyFieldLabel>
              <Input
                className="glass-input h-10"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                maxLength={10}
                disabled={saving}
              />
            </div>
          </div>

          <DialogFooter className="pt-4 border-t border-border/50 gap-2">
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSaveState()}
              disabled={saving || !newName.trim() || !country}
              className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
            >
              {saving ? "Saving…" : "Save state"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
